// SwipeBack — 전역 좌측 엣지 스와이프 뒤로가기 + 인터랙티브 트랜지션 (P2 §30)
// iOS WKWebView는 SPA(react-router) 히스토리에 네이티브 엣지 스와이프가 안 먹어,
// 화면 좌측 끝에서 시작하는 우향 드래그를 감지해 공용 back resolver를 실행한다.
// 2026-06-24: 효신님 실기 피드백 — 화면이 손가락을 따라 밀리는 인터랙티브 느낌이 없어 어색.
//   → 드래그 중 #screen-stage를 translateX로 따라 움직이고(+좌측 그림자), 놓을 때
//     충분히 끌었으면 오른쪽으로 슬라이드 아웃 후 공용 back resolver 실행, 아니면 제자리 복귀.
//   라이브러리 무도입(이전 화면 DOM이 없어 완전한 iOS 트랜지션은 불가 — '끌리는 피드백'까지).
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useBackTo } from '../hooks/useBackTo';
import { isTabRoot } from '../lib/tabNavigation';
import { probeMark } from './HistoryProbe'; // 계측 빌드에서만 동작(평소 no-op)

// 탭 루트(하단 네비 5개) — 돌아갈 상위가 없어 스와이프 뒤로가기 비활성.
//   2026-09-07: 하드코딩 Set 폐기 → tabNavigation.isTabRoot (금융 루트 제거·복기 루트 추가가 사전 한 곳에서 반영된다).

const EDGE = 24;         // 좌측 엣지 시작 폭(px) — 가로 스크롤(차트·칩 row) 충돌 회피
const COMMIT_RATIO = 0.4; // 화면 폭의 40% 이상 끌면 뒤로가기 확정
const MAX_MS = 800;      // 빠른 스와이프만(느린 드래그 오발 방지)

export default function SwipeBack() {
  const back = useBackTo();
  const location = useLocation();
  const start = useRef<{ x: number; y: number; t: number } | null>(null);
  const dragging = useRef(false);
  const lastDx = useRef(0);

  useEffect(() => {
    const getStage = () => document.getElementById('screen-stage');

    // 평상시엔 transform을 '제거'한다(translateX(0)도 transform이라 position:fixed 자식의
    // containing block을 바꿔버림 → 헤더 어긋남). 그래서 끝나면 빈 문자열로 되돌린다.
    const clearStage = (animate: boolean) => {
      const el = getStage();
      if (!el) return;
      if (animate) {
        el.style.transition = 'transform 0.22s ease';
        el.style.transform = 'translateX(0)';
        el.style.boxShadow = '';
        window.setTimeout(() => {
          el.style.transition = '';
          el.style.transform = '';
        }, 230);
      } else {
        el.style.transition = '';
        el.style.transform = '';
        el.style.boxShadow = '';
      }
    };

    const onStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) { start.current = null; return; }
      const t = e.touches[0];
      const modalOpen = !!document.querySelector('.cs-modal-overlay');
      if (t.clientX <= EDGE && !isTabRoot(location.pathname) && !modalOpen) {
        start.current = { x: t.clientX, y: t.clientY, t: performance.now() };
        dragging.current = false;
        lastDx.current = 0;
      } else {
        start.current = null;
      }
    };

    const onMove = (e: TouchEvent) => {
      if (!start.current || e.touches.length !== 1) return;
      const t = e.touches[0];
      const dx = t.clientX - start.current.x;
      const dy = Math.abs(t.clientY - start.current.y);
      // 수평 우향 우세일 때만 화면을 손가락 따라 이동(세로 스크롤과 공존 — passive라 스크롤은 막지 않음)
      if (dx > 6 && dx > dy * 1.2) {
        dragging.current = true;
        lastDx.current = dx;
        const el = getStage();
        if (el) {
          el.style.transition = 'none';
          el.style.transform = `translateX(${dx}px)`;
          el.style.boxShadow = '-12px 0 28px rgba(0,0,0,0.18)';
        }
      }
    };

    const onEnd = () => {
      const s = start.current;
      const dx = lastDx.current;
      start.current = null;
      if (!dragging.current || !s) { dragging.current = false; return; }
      dragging.current = false;
      const fast = performance.now() - s.t < MAX_MS;
      const width = window.innerWidth || 360;
      if (dx > width * COMMIT_RATIO && fast) {
        // 확정 — 오른쪽으로 끝까지 밀어내고 뒤로가기
        const el = getStage();
        if (el) {
          el.style.transition = 'transform 0.18s ease';
          el.style.transform = `translateX(${width}px)`;
        }
        probeMark(`swipeback commit dx=${Math.round(dx)}`);
        window.setTimeout(() => { back(); clearStage(false); }, 180);
      } else {
        // 미달 — 제자리 복귀
        clearStage(true);
      }
    };

    document.addEventListener('touchstart', onStart, { passive: true });
    document.addEventListener('touchmove', onMove, { passive: true });
    document.addEventListener('touchend', onEnd, { passive: true });
    document.addEventListener('touchcancel', onEnd, { passive: true });
    return () => {
      document.removeEventListener('touchstart', onStart);
      document.removeEventListener('touchmove', onMove);
      document.removeEventListener('touchend', onEnd);
      document.removeEventListener('touchcancel', onEnd);
    };
  }, [back, location.pathname]);

  return null;
}
